import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Users, Wallet, ArrowDownCircle, ArrowUpCircle, Ticket, Trophy, TrendingUp, TrendingDown, Download, FileText, Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { usePageLoadMetric } from "@/lib/diagnostics/usePageLoadMetric";

export const Route = createFileRoute("/admin/")({
  head: () => ({ meta: [{ title: "Admin Dashboard" }] }),
  component: AdminDashboard,
});

type Preset = "today" | "yesterday" | "7d" | "30d" | "custom";

function rangeFor(preset: Preset, custom: { from?: Date; to?: Date }): { from: Date; to: Date; label: string } {
  const now = new Date();
  if (preset === "today") return { from: startOfDay(now), to: endOfDay(now), label: "Today" };
  if (preset === "yesterday") { const y = subDays(now, 1); return { from: startOfDay(y), to: endOfDay(y), label: "Yesterday" }; }
  if (preset === "7d") return { from: startOfDay(subDays(now, 6)), to: endOfDay(now), label: "Last 7 Days" };
  if (preset === "30d") return { from: startOfDay(subDays(now, 29)), to: endOfDay(now), label: "Last 30 Days" };
  const from = custom.from ? startOfDay(custom.from) : startOfDay(subDays(now, 6));
  const to = custom.to ? endOfDay(custom.to) : endOfDay(now);
  return { from, to, label: `${format(from, "dd MMM")} – ${format(to, "dd MMM")}` };
}

function fmtINR(n: number) { return `₹${Math.round(n).toLocaleString("en-IN")}`; }

function toCSV(rows: Record<string, any>[]): string {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const esc = (v: any) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
}

function download(name: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

function AdminDashboard() {
  const [preset, setPreset] = useState<Preset>("7d");
  const [custom, setCustom] = useState<{ from?: Date; to?: Date }>({});
  const range = useMemo(() => rangeFor(preset, custom), [preset, custom]);

  // ---- Totals (all-time)
  const { data: totals } = useQuery({
    queryKey: ["admin_totals"],
    queryFn: async () => {
      const [{ count: users }, { data: profs }, { data: txns }, { data: bets }] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("profiles").select("wallet_balance,updated_at"),
        supabase.from("wallet_transactions").select("type,amount,status"),
        supabase.from("bets").select("amount,payout,status"),
      ]);
      const sumTx = (t: string) => (txns ?? []).filter((x: any) => x.type === t && x.status === "success").reduce((s: number, x: any) => s + Number(x.amount), 0);
      const totalBets = (bets ?? []).reduce((s: number, b: any) => s + Number(b.amount), 0);
      const totalWin = (bets ?? []).filter((b: any) => b.status === "won").reduce((s: number, b: any) => s + Number(b.payout), 0);
      const wallet = (profs ?? []).reduce((s: number, p: any) => s + Number(p.wallet_balance), 0);
      const active = (profs ?? []).filter((p: any) => new Date(p.updated_at).getTime() > Date.now() - 7 * 86400_000).length;
      return {
        users: users ?? 0,
        active,
        deposit: sumTx("add"),
        withdraw: sumTx("withdraw"),
        totalBets,
        totalWin,
        profit: totalBets - totalWin,
        wallet,
      };
    },
  });

  usePageLoadMetric("admin_dashboard_load", !totals);

  // ---- Range data
  const { data: rangeData } = useQuery({
    queryKey: ["admin_range", range.from.toISOString(), range.to.toISOString()],
    queryFn: async () => {
      const fromIso = range.from.toISOString();
      const toIso = range.to.toISOString();
      const [{ data: txns }, { data: bets }, { data: users }] = await Promise.all([
        supabase.from("wallet_transactions").select("type,amount,status,created_at,user_id").gte("created_at", fromIso).lte("created_at", toIso),
        supabase.from("bets").select("amount,payout,status,created_at,game_id,user_id,games(name,color)").gte("created_at", fromIso).lte("created_at", toIso),
        supabase.from("profiles").select("id,created_at").gte("created_at", fromIso).lte("created_at", toIso),
      ]);
      return { txns: txns ?? [], bets: bets ?? [], users: users ?? [] };
    },
  });

  // ---- Today
  const today = useMemo(() => rangeFor("today", {}), []);
  const { data: todayData } = useQuery({
    queryKey: ["admin_today", today.from.toISOString()],
    queryFn: async () => {
      const [{ data: txns }, { data: bets }] = await Promise.all([
        supabase.from("wallet_transactions").select("type,amount,status").gte("created_at", today.from.toISOString()).lte("created_at", today.to.toISOString()),
        supabase.from("bets").select("amount,payout,status").gte("created_at", today.from.toISOString()).lte("created_at", today.to.toISOString()),
      ]);
      const sumTx = (t: string) => (txns ?? []).filter((x: any) => x.type === t && x.status === "success").reduce((s: number, x: any) => s + Number(x.amount), 0);
      const betAmt = (bets ?? []).reduce((s: number, b: any) => s + Number(b.amount), 0);
      const win = (bets ?? []).filter((b: any) => b.status === "won").reduce((s: number, b: any) => s + Number(b.payout), 0);
      return { deposit: sumTx("add"), withdraw: sumTx("withdraw"), bets: betAmt, win, profit: betAmt - win };
    },
  });

  // ---- Users report (range-scoped)
  const { data: userReport } = useQuery({
    queryKey: ["admin_users_report", range.from.toISOString(), range.to.toISOString()],
    queryFn: async () => {
      const { data: profs } = await supabase.from("profiles").select("id,name,phone,wallet_balance");
      const [{ data: txns }, { data: bets }] = await Promise.all([
        supabase.from("wallet_transactions").select("user_id,type,amount,status").gte("created_at", range.from.toISOString()).lte("created_at", range.to.toISOString()),
        supabase.from("bets").select("user_id,amount,payout,status").gte("created_at", range.from.toISOString()).lte("created_at", range.to.toISOString()),
      ]);
      const map: Record<string, any> = {};
      for (const p of profs ?? []) map[p.id] = { name: p.name ?? "—", phone: p.phone ?? "—", wallet: Number(p.wallet_balance), deposit: 0, withdraw: 0, bet: 0, win: 0 };
      for (const t of txns ?? []) {
        const u = map[t.user_id]; if (!u || t.status !== "success") continue;
        if (t.type === "add") u.deposit += Number(t.amount);
        else if (t.type === "withdraw") u.withdraw += Number(t.amount);
      }
      for (const b of bets ?? []) {
        const u = map[b.user_id]; if (!u) continue;
        u.bet += Number(b.amount);
        if (b.status === "won") u.win += Number(b.payout);
      }
      return Object.values(map).sort((a: any, b: any) => b.bet - a.bet);
    },
  });

  // ---- Derived
  const gameRows = useMemo(() => {
    const map: Record<string, { name: string; color: string; bets: number; collection: number; win: number }> = {};
    for (const b of rangeData?.bets ?? []) {
      const g = (b as any).games; const key = b.game_id;
      if (!map[key]) map[key] = { name: g?.name ?? "—", color: g?.color ?? "#7c3aed", bets: 0, collection: 0, win: 0 };
      map[key].bets += 1;
      map[key].collection += Number(b.amount);
      if (b.status === "won") map[key].win += Number(b.payout);
    }
    return Object.values(map).map((g) => ({ ...g, profit: g.collection - g.win })).sort((a, b) => b.collection - a.collection);
  }, [rangeData]);

  const chartData = useMemo(() => {
    const days: Record<string, { date: string; deposit: number; withdraw: number; profit: number; users: number }> = {};
    const cursor = new Date(range.from);
    while (cursor <= range.to) {
      const k = format(cursor, "yyyy-MM-dd");
      days[k] = { date: format(cursor, "dd MMM"), deposit: 0, withdraw: 0, profit: 0, users: 0 };
      cursor.setDate(cursor.getDate() + 1);
    }
    for (const t of rangeData?.txns ?? []) {
      const k = format(new Date(t.created_at), "yyyy-MM-dd"); if (!days[k] || t.status !== "success") continue;
      if (t.type === "add") days[k].deposit += Number(t.amount);
      else if (t.type === "withdraw") days[k].withdraw += Number(t.amount);
    }
    for (const b of rangeData?.bets ?? []) {
      const k = format(new Date(b.created_at), "yyyy-MM-dd"); if (!days[k]) continue;
      days[k].profit += Number(b.amount);
      if (b.status === "won") days[k].profit -= Number(b.payout);
    }
    for (const u of rangeData?.users ?? []) {
      const k = format(new Date(u.created_at), "yyyy-MM-dd"); if (!days[k]) continue;
      days[k].users += 1;
    }
    // cumulative user growth
    let cum = 0;
    return Object.values(days).map((d) => { cum += d.users; return { ...d, cumUsers: cum }; });
  }, [rangeData, range]);

  function exportCSV(kind: "games" | "users") {
    const rows = kind === "games"
      ? gameRows.map((g) => ({ Game: g.name, Bets: g.bets, Collection: g.collection, Winning: g.win, Profit: g.profit }))
      : (userReport ?? []).map((u: any) => ({ Name: u.name, Phone: u.phone, Deposit: u.deposit, Withdraw: u.withdraw, Bet: u.bet, Winning: u.win, Wallet: u.wallet }));
    download(`${kind}-report-${format(range.from, "yyyyMMdd")}-${format(range.to, "yyyyMMdd")}.csv`, toCSV(rows), "text/csv");
  }

  function exportPDF(kind: "games" | "users") {
    const rows = kind === "games"
      ? gameRows.map((g) => ({ Game: g.name, Bets: g.bets, Collection: fmtINR(g.collection), Winning: fmtINR(g.win), Profit: fmtINR(g.profit) }))
      : (userReport ?? []).map((u: any) => ({ Name: u.name, Phone: u.phone, Deposit: fmtINR(u.deposit), Withdraw: fmtINR(u.withdraw), Bet: fmtINR(u.bet), Winning: fmtINR(u.win), Wallet: fmtINR(u.wallet) }));
    const title = kind === "games" ? "Game Report" : "User Report";
    const headers = rows[0] ? Object.keys(rows[0]) : [];
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
      <style>body{font-family:system-ui,sans-serif;padding:24px;color:#111}h1{margin:0 0 4px}p{margin:0 0 16px;color:#666;font-size:12px}
      table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
      th{background:#f5f5f5}@media print{button{display:none}}</style></head>
      <body><h1>${title}</h1><p>${range.label} · Generated ${format(new Date(), "dd MMM yyyy, HH:mm")}</p>
      <button onclick="window.print()">Print / Save as PDF</button>
      <table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
      <tbody>${rows.map((r) => `<tr>${headers.map((h) => `<td>${(r as any)[h] ?? ""}</td>`).join("")}</tr>`).join("")}</tbody></table>
      <script>setTimeout(()=>window.print(),300)</script></body></html>`;
    const w = window.open("", "_blank"); if (!w) return; w.document.write(html); w.document.close();
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black">Dashboard & Reports</h1>
          <p className="text-sm text-muted-foreground">Overview and analytics for {range.label.toLowerCase()}</p>
        </div>
        <RangePicker preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} label={range.label} />
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Total Users" value={totals?.users ?? 0} icon={<Users className="h-4 w-4" />} tone="text-primary" />
        <Stat label="Active Users (7d)" value={totals?.active ?? 0} icon={<Users className="h-4 w-4" />} tone="text-success" />
        <Stat label="Total Deposit" value={fmtINR(totals?.deposit ?? 0)} icon={<ArrowDownCircle className="h-4 w-4" />} tone="text-primary" />
        <Stat label="Total Withdraw" value={fmtINR(totals?.withdraw ?? 0)} icon={<ArrowUpCircle className="h-4 w-4" />} tone="text-destructive" />
        <Stat label="Total Bets" value={fmtINR(totals?.totalBets ?? 0)} icon={<Ticket className="h-4 w-4" />} tone="text-warning" />
        <Stat label="Winning Paid" value={fmtINR(totals?.totalWin ?? 0)} icon={<Trophy className="h-4 w-4" />} tone="text-warning" />
        <Stat label="Company P/L" value={fmtINR(totals?.profit ?? 0)}
          icon={(totals?.profit ?? 0) >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          tone={(totals?.profit ?? 0) >= 0 ? "text-success" : "text-destructive"} />
        <Stat label="Wallet Balance" value={fmtINR(totals?.wallet ?? 0)} icon={<Wallet className="h-4 w-4" />} tone="text-primary" />
      </div>

      {/* Today report */}
      <Card title="Today's Report">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Mini label="Deposit" value={fmtINR(todayData?.deposit ?? 0)} tone="text-primary" />
          <Mini label="Withdraw" value={fmtINR(todayData?.withdraw ?? 0)} tone="text-destructive" />
          <Mini label="Bets" value={fmtINR(todayData?.bets ?? 0)} tone="text-warning" />
          <Mini label="Winning" value={fmtINR(todayData?.win ?? 0)} tone="text-warning" />
          <Mini label="Profit" value={fmtINR(todayData?.profit ?? 0)} tone={(todayData?.profit ?? 0) >= 0 ? "text-success" : "text-destructive"} />
        </div>
      </Card>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Deposit vs Withdraw">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Legend />
                <Bar dataKey="deposit" fill="hsl(var(--primary))" name="Deposit" />
                <Bar dataKey="withdraw" fill="hsl(var(--destructive))" name="Withdraw" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Company Profit">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="profit" stroke="hsl(var(--success))" strokeWidth={2} name="Profit" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="User Growth">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="cumUsers" stroke="hsl(var(--primary))" strokeWidth={2} name="Cumulative Users" />
                <Line type="monotone" dataKey="users" stroke="hsl(var(--warning))" strokeWidth={2} name="New / day" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Daily Bets vs Winnings (from Deposit chart totals)">
          <div className="h-64 grid place-items-center text-xs text-muted-foreground">
            <div className="text-center">
              <p>Range totals</p>
              <p className="mt-2 text-base font-bold text-foreground">
                Deposit {fmtINR(chartData.reduce((s, d) => s + d.deposit, 0))} · Withdraw {fmtINR(chartData.reduce((s, d) => s + d.withdraw, 0))}
              </p>
              <p className="mt-1 text-base font-bold text-foreground">
                Profit {fmtINR(chartData.reduce((s, d) => s + d.profit, 0))}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Game report */}
      <Card
        title="Game Report"
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => exportCSV("games")}><Download className="mr-1 h-3.5 w-3.5" />CSV</Button>
            <Button size="sm" variant="outline" onClick={() => exportPDF("games")}><FileText className="mr-1 h-3.5 w-3.5" />PDF</Button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr><th className="pb-2">Game</th><th className="pb-2">Bets</th><th className="pb-2">Collection</th><th className="pb-2">Winning</th><th className="pb-2">Profit / Loss</th></tr>
            </thead>
            <tbody>
              {gameRows.length === 0 && (<tr><td colSpan={5} className="py-4 text-center text-muted-foreground">No data</td></tr>)}
              {gameRows.map((g) => (
                <tr key={g.name} className="border-t">
                  <td className="py-2 font-semibold"><span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: g.color }} />{g.name}</td>
                  <td className="py-2">{g.bets}</td>
                  <td className="py-2">{fmtINR(g.collection)}</td>
                  <td className="py-2">{fmtINR(g.win)}</td>
                  <td className={cn("py-2 font-bold", g.profit >= 0 ? "text-success" : "text-destructive")}>{fmtINR(g.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* User report */}
      <Card
        title="User Report"
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => exportCSV("users")}><Download className="mr-1 h-3.5 w-3.5" />CSV</Button>
            <Button size="sm" variant="outline" onClick={() => exportPDF("users")}><FileText className="mr-1 h-3.5 w-3.5" />PDF</Button>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr><th className="pb-2">Name</th><th className="pb-2">Mobile</th><th className="pb-2">Deposit</th><th className="pb-2">Withdraw</th><th className="pb-2">Bet</th><th className="pb-2">Winning</th><th className="pb-2">Wallet</th></tr>
            </thead>
            <tbody>
              {(userReport ?? []).length === 0 && (<tr><td colSpan={7} className="py-4 text-center text-muted-foreground">No data</td></tr>)}
              {(userReport ?? []).slice(0, 100).map((u: any, i: number) => (
                <tr key={i} className="border-t">
                  <td className="py-2 font-semibold">{u.name}</td>
                  <td className="py-2 text-muted-foreground">{u.phone}</td>
                  <td className="py-2">{fmtINR(u.deposit)}</td>
                  <td className="py-2">{fmtINR(u.withdraw)}</td>
                  <td className="py-2">{fmtINR(u.bet)}</td>
                  <td className="py-2">{fmtINR(u.win)}</td>
                  <td className="py-2 font-bold">{fmtINR(u.wallet)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function RangePicker({ preset, setPreset, custom, setCustom, label }: { preset: Preset; setPreset: (p: Preset) => void; custom: { from?: Date; to?: Date }; setCustom: (v: { from?: Date; to?: Date }) => void; label: string }) {
  const opts: { k: Preset; l: string }[] = [
    { k: "today", l: "Today" }, { k: "yesterday", l: "Yesterday" }, { k: "7d", l: "7 Days" }, { k: "30d", l: "30 Days" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {opts.map((o) => (
        <button key={o.k} onClick={() => setPreset(o.k)}
          className={cn("rounded-full px-3 py-1.5 text-xs font-bold uppercase transition-colors",
            preset === o.k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70")}>
          {o.l}
        </button>
      ))}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant={preset === "custom" ? "default" : "outline"} size="sm" className="h-8">
            <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
            {preset === "custom" ? label : "Custom"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar mode="range" selected={{ from: custom.from, to: custom.to }}
            onSelect={(r: any) => { setCustom({ from: r?.from, to: r?.to }); if (r?.from && r?.to) setPreset("custom"); }}
            numberOfMonths={1} className={cn("p-3 pointer-events-auto")} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function Stat({ label, value, icon, tone }: { label: string; value: any; icon: React.ReactNode; tone: string }) {
  return (
    <div className="rounded-2xl bg-card p-4 shadow-card">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{label}</p>
        <span className={tone}>{icon}</span>
      </div>
      <p className="mt-2 text-xl font-black">{value}</p>
    </div>
  );
}

function Mini({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="rounded-xl bg-muted/40 p-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn("mt-1 text-base font-black", tone)}>{value}</p>
    </div>
  );
}

function Card({ title, children, actions }: { title: string; children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-card p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold">{title}</h3>
        {actions}
      </div>
      {children}
    </div>
  );
}
